import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { api } from '@connected/api-client';
import { validators } from '@connected/api-client/generated/validators.generated';
import { usePatcher } from '@connected/api-client/hooks/use-patcher';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { DateTimeField } from '@connected/form-fields/components/datetime';
import FieldInfo from '@connected/form-fields/components/field-info';
import { Label } from '@connected/form-fields/components/label';
import {
    MultipleOptionField,
    MultipleOptionItem,
    SingleOptionField,
    SingleOptionItem,
} from '@connected/form-fields/components/option';
import { Button } from '@connected/ui/components/button';
import { Section } from '@connected/ui/features/layouts';
import { Container } from '@connected/ui/layouts/container';
import { booleanDefaultValue } from '@connected/ui/lib/utils';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FooterNavigation } from '@/components/navigation/footer-navigation';
import { queryClient } from '@/lib/query-client';
import { useOffline } from '@/stores/network-status';

type FormValues = {
    isOrderConfirmed: boolean | null;
    startDate: Date;
    executors: string[];
};

export const Route = createFileRoute('/(auth)/projects/$projectVersionId/schedules/')({
    component: Page,
    loader: ({ context, params }) => {
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.schedule.scheduleOptions({ electricalProjectVersionId: params.projectVersionId }));
        context.queryClient.ensureQueryData(elektraAppQueryOptions.project.schedule.projectUsersListOptions({ electricalProjectVersionId: params.projectVersionId, userKind: 'Inspector' }));
    },
});

function Page() {
    const { t } = useTranslation('schedules');

    return (
        <Container>
            <Container.Header>
                <Container.Title>{t('layout.title')}</Container.Title>
            </Container.Header>
            <Container.Content>
                <Form />
            </Container.Content>
            <Section.Footer>
                <FooterNavigation />
            </Section.Footer>
        </Container>
    );
}

function Form() {
    const { t } = useTranslation('schedules');

    const isUserOffline = useOffline();

    const params = Route.useParams();

    const scheduleInformation = useQuery(elektraAppQueryOptions.project.schedule.scheduleOptions({ electricalProjectVersionId: params.projectVersionId }));
    const scheduleInformationData = scheduleInformation?.data?.Schedule;

    const inspectorUsers = useQuery(elektraAppQueryOptions.project.schedule.projectUsersListOptions({ electricalProjectVersionId: params.projectVersionId, userKind: 'Inspector' }));

    const usersData = inspectorUsers?.data?.Users || [];

    const userOptions
        = usersData?.map(user => ({
            label: user.DisplayName!,
            value: user.UserId!,
        })) || [];

    const [selectedUsers, setSelectedUsers] = useState<string[]>([...(usersData?.filter(x => x.Selected === true)?.map(x => x.UserId!) || [])]);

    const { patchField } = usePatcher('/api/v2/electrical/projects/inspections/schedules/patch', {
        onSettled: () => {
            queryClient.invalidateQueries(elektraAppQueryOptions.project.schedule.scheduleOptions({ electricalProjectVersionId: params.projectVersionId }));
        },
    }, {
        body: {
            ElectricalProjectVersionId: params.projectVersionId,
        },
    });

    const selectedProjectUserMutation = api.useMutation(
        'put',
        '/api/v2/electrical/projects/users/edit',
        {
            onSettled: () => {
                queryClient.invalidateQueries(elektraAppQueryOptions.project.schedule.projectUsersListOptions({ electricalProjectVersionId: params.projectVersionId, userKind: 'Inspector' }));
            },
        },
    );

    function handleUserChange(values: string[]) {
        const existingSelected = selectedUsers;
        const newSelected = values;

        const toAdd = newSelected.filter(x => !existingSelected.includes(x));
        const toRemove = existingSelected.filter(
            x => !newSelected.includes(x),
        );

        setSelectedUsers(newSelected);

        toAdd.forEach(async (item) => {
            await selectedProjectUserMutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    UserId: item,
                    Selected: true,
                    Kind: usersData.find(x => x.UserId === item)?.Kind || 'Inspector',

                },
            });
        });

        toRemove.forEach(async (item) => {
            await selectedProjectUserMutation.mutateAsync({
                body: {
                    ElectricalProjectVersionId: params.projectVersionId,
                    UserId: item,
                    Selected: false,
                    Kind: usersData.find(x => x.UserId === item)?.Kind || 'Inspector',
                },
            });
        });
    }

    const { Field, Subscribe } = useForm<
        FormValues,
        ZodValidator
    >({
        defaultValues: {
            isOrderConfirmed: booleanDefaultValue(scheduleInformationData?.IsOrderConfirmed),
            startDate: scheduleInformationData?.StartDate
                ? parseISO(scheduleInformationData.StartDate)
                : new Date(),
            executors: [],
        },
        validatorAdapter: zodValidator(),
    });

    return (
        <div className="flex flex-col gap-4">
            <Field
                name="isOrderConfirmed"
                validators={{
                    onChange:
                        validators.editElectricalProjectInspectionScheduleCommand.isOrderConfirmed,
                    onChangeAsync: patchField('IsOrderConfirmed'),
                    onChangeAsyncDebounceMs: patchField.debounceTime,
                }}
            >
                {({ handleChange, state, name }) => (
                    <Label
                        label={t('form.assignmentConfirmed.label')}
                        htmlFor={name}
                    >
                        <SingleOptionField
                            name={name}
                            handleChange={(values) => {
                                handleChange(values);
                            }}
                            state={state}
                            options={[
                                {
                                    label: 'Ja',
                                    value: true,
                                },
                                {
                                    label: 'Nee',
                                    value: false,
                                },
                            ]}
                            render={SingleOptionItem}
                            className="flex-row gap-2"
                            disabled={isUserOffline}
                        />
                        <FieldInfo state={state} />
                    </Label>
                )}
            </Field>
            <Subscribe selector={state => [state.values.isOrderConfirmed]}>
                {([assignmentConfirmed]) =>
                    assignmentConfirmed && (
                        <div className="grid grid-cols-3 gap-4">
                            <Field
                                name="startDate"
                                validators={{
                                    onChange:
                                        validators.editElectricalProjectInspectionScheduleCommand.startDate,
                                    onChangeAsync: patchField(
                                        'StartDate',
                                        (value: unknown) =>
                                            format(value as Date, 'yyyy-MM-dd\'T\'HH:mm:ss'),
                                    ),
                                    onChangeAsyncDebounceMs: patchField.debounceTime,
                                }}
                            >
                                {({ handleChange, state }) => (
                                    <Label label={t('form.startingDate.label')} className="w-full">
                                        <DateTimeField
                                            value={state.value}
                                            onChange={handleChange}
                                        />
                                        <FieldInfo state={state} />
                                    </Label>
                                )}
                            </Field>
                            <Label
                                label={t('form.executors.label')}
                                className="w-full col-span-2"
                            >
                                <MultipleOptionField
                                    handleChange={(values) => {
                                        handleUserChange(values);
                                    }}
                                    state={{ value: selectedUsers }}
                                    options={userOptions}
                                    render={MultipleOptionItem}
                                    dropdown
                                    emptyOptionsMessage={t('form.executors.emptyMessage')}
                                    disabled={isUserOffline}
                                />
                            </Label>
                        </div>
                    )}
            </Subscribe>
        </div>
    );
}

function Footer() {
    const { t } = useTranslation('common');

    const params = Route.useParams();
    const search = Route.useSearch();

    return (
        <footer className="flex justify-between gap-4">
            <Button variant="ghost" asChild>
                <Link
                    to="/projects/$projectVersionId/inspection-plans"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.previous')}
                </Link>
            </Button>
            <Button asChild variant="ghost">
                <Link
                    to="/projects/$projectVersionId/re-inspections/information"
                    params={params}
                    search={search}
                >
                    {t('actions.navigation.next')}
                </Link>
            </Button>
        </footer>
    );
}
